import React, { useState } from 'react';

import { Flex, Space, Switch, TextInput, useMantineTheme } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconCheck, IconKey } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { UpgradePlanBlurCard } from 'components/UpgradePlanBlurCard';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { t } from 'i18next';
import { BringYourOwnKeyService } from 'services/byok-service';
import { NotificationService } from 'services/notification-service';
import { OPEN_ROUTER_BYOK_UPDATE_NOTIF_ID } from 'shared/constants/notification-ids';
import { BYOK_OPEN_ROUTER_QUERY_KEY } from 'shared/constants/query-keys';
import { validateOpenRouterAPIKey } from 'shared/utils/form-validators';

export function OpenRouterApiKeySettings() {
  const [subscription] = useSubscriptionContext();
  const [isKeyActive, setIsKeyActive] = useState(false);
  const theme = useMantineTheme();

  useQuery({
    queryFn: BringYourOwnKeyService.getOpenRouterApiKey,
    queryKey: [BYOK_OPEN_ROUTER_QUERY_KEY],
    onSuccess: (openRouterApiKey) => {
      if (openRouterApiKey) {
        keyForm.setValues({ key: openRouterApiKey });
        setIsKeyActive(true);
      }
    },
    enabled: !!subscription.product.enableByok
  });

  const setOpenRouterKeyMutation = useMutation({
    mutationFn: BringYourOwnKeyService.setOpenRouterApiKey,
    onSuccess: () => {
      setIsKeyActive(true);
      NotificationService.endAction(true, OPEN_ROUTER_BYOK_UPDATE_NOTIF_ID, 'FEED_BACK.BYOK.SUCCESS.UPDATE');
    },
    onError: () => {
      keyForm.setErrors({ key: t('FORM_VALIDATION.INVALID_OPEN_ROUTER_API_KEY') });
      NotificationService.endAction(false, OPEN_ROUTER_BYOK_UPDATE_NOTIF_ID, 'FEED_BACK.BYOK.FAILURE.UPDATE');
    }
  });

  const deleteOpenRouterKeyMutation = useMutation({
    mutationFn: BringYourOwnKeyService.unsetOpenRouterApiKey,
    onSuccess: () => {
      setIsKeyActive(false);
      keyForm.reset();
      NotificationService.endAction(true, OPEN_ROUTER_BYOK_UPDATE_NOTIF_ID, 'FEED_BACK.BYOK.SUCCESS.DELETE');
    },
    onError: () => {
      NotificationService.endAction(false, OPEN_ROUTER_BYOK_UPDATE_NOTIF_ID, 'FEED_BACK.BYOK.FAILURE.DELETE');
    }
  });

  const keyForm = useForm({
    initialValues: { key: '' },
    validate: {
      key: (value) => validateOpenRouterAPIKey(value)
    }
  });

  const handleOpenRouterApiKeyActivationStatusChange = (activated: boolean) => {
    if (activated) {
      const formValidation = keyForm.validate();
      if (!formValidation.hasErrors) setOpenRouterKeyMutation.mutate(keyForm.values.key);
    } else {
      deleteOpenRouterKeyMutation.mutate();
    }
  };

  return (
    <>
      <UpgradePlanBlurCard message={t('BYOK.UPGRADE.DESCRIPTION')} showCard={!subscription.product.enableByok}>
        <Flex direction="row" align="flex-start" mih={400}>
          <TextInput
            w="100%"
            maw={350}
            icon={<IconKey />}
            variant="default"
            placeholder={t('BYOK.OPEN_ROUTER_API_KEY.PLACEHOLDER')}
            {...keyForm.getInputProps('key')}
          />
          <Space w={20} />
          <Switch
            mt={7}
            w="50%"
            checked={isKeyActive}
            onChange={(event) => handleOpenRouterApiKeyActivationStatusChange(event.currentTarget.checked)}
            color="teal"
            label={t('BYOK.ACTIVATE_KEY')}
            thumbIcon={
              isKeyActive && <IconCheck size={12} color={theme.colors.teal[theme.fn.primaryShade()]} stroke={3} />
            }
          />
        </Flex>
      </UpgradePlanBlurCard>
    </>
  );
}
